import { adminTenantId, Theme } from '@logto/schemas';
import { useEffect, useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import { getOverseasBrand } from '@/hooks/use-native-sign-in';
import type { SignInExperienceResponse } from '@/types';

const prefersDarkSchemeQuery = '(prefers-color-scheme: dark)';

const getDarkThemeWatchMedia = (): MediaQueryList | undefined => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return undefined;
  }

  return window.matchMedia(prefersDarkSchemeQuery);
};

export const getThemeBySystemConfiguration = (): Theme =>
  getDarkThemeWatchMedia()?.matches ? Theme.Dark : Theme.Light;

export const shouldForceLightTheme = (
  settings?: Pick<SignInExperienceResponse, 'tenantId' | 'customContent'>
) =>
  settings?.tenantId !== adminTenantId &&
  Boolean(getOverseasBrand(settings?.customContent.evaEdition));

export default function useTheme() {
  const { isPreview, experienceSettings, setTheme } = useContext(PageContext);

  useEffect(() => {
    if (shouldForceLightTheme(experienceSettings)) {
      setTheme(Theme.Light);
      return;
    }

    if (!experienceSettings?.color.isDarkModeEnabled) {
      return;
    }

    const changeTheme = () => {
      setTheme(getThemeBySystemConfiguration());
    };

    changeTheme();

    const darkThemeWatchMedia = getDarkThemeWatchMedia();

    if (!darkThemeWatchMedia) {
      return;
    }

    darkThemeWatchMedia.addEventListener('change', changeTheme);

    return () => {
      darkThemeWatchMedia.removeEventListener('change', changeTheme);
    };
  }, [experienceSettings, isPreview, setTheme]);
}
